// Fixture data + helpers. Phase 2 mock-state lives entirely in this folder;
// Phase 5 deletion is `rm -rf packages/agent-app/src/mock`.

import type { VisitorId, VisitorStatus } from '@prakash-software/shared';
import { useVisitorsStore, type VisitorRow } from '../stores/visitors';

const NAMES = [
  'Asha Iyer',
  'Kabir Bose',
  'Maya Reddy',
  'Devansh Patel',
  'Priya Nair',
  'Rohit Gupta',
  'Anjali Joshi',
  'Vikram Singh',
  'Ishita Roy',
  'Tara Menon',
] as const;

const SITES = ['prakashsoftware.com', 'careers.prakashsoftware.com'] as const;
const PAGES_BY_SITE: Record<(typeof SITES)[number], readonly string[]> = {
  'prakashsoftware.com': [
    '/',
    '/services/cloud',
    '/services/ai',
    '/case-studies/migration',
    '/pricing',
    '/contact',
  ],
  'careers.prakashsoftware.com': ['/', '/openings/senior-engineer', '/culture', '/apply'],
};

const STATUSES: readonly VisitorStatus[] = ['browsing', 'engaged', 'idle'];

const PLACES = [
  ['Pune', 'Maharashtra', 'India'],
  ['Bengaluru', 'Karnataka', 'India'],
  ['Mumbai', 'Maharashtra', 'India'],
  ['Singapore', 'Central', 'Singapore'],
  ['London', 'England', 'United Kingdom'],
  ['Austin', 'Texas', 'United States'],
  ['Berlin', 'Berlin', 'Germany'],
  ['Toronto', 'Ontario', 'Canada'],
] as const;

const SUMMARIES = [
  'Last visit: asked about pricing for Cloud migration. Pre-sales handoff pending.',
  'Previous chat: explored AI POC. Wants to revisit in Q3.',
  'Returning from career page — confirmed mid-senior role.',
] as const;

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function makeVisitorId(): VisitorId {
  return (`v_${Math.random().toString(36).slice(2, 8)}` as unknown) as VisitorId;
}

export function makeFixtureVisitor(opts: { now?: number } = {}): VisitorRow {
  const name = pick(NAMES);
  const site = pick(SITES);
  const page = pick(PAGES_BY_SITE[site]);
  const place = pick(PLACES);
  const isReturning = Math.random() < 0.35;
  const status = pick(STATUSES);
  const now = opts.now ?? Date.now();
  const arrivedAt = now - Math.floor(Math.random() * 300_000); // up to 5min ago

  return {
    identity: {
      visitorId: makeVisitorId(),
      name: Math.random() < 0.6 ? name : undefined,
      isReturning,
      geo: { city: place[0], region: place[1], country: place[2] },
    },
    context: {
      origin: `https://${site}`,
      pagePath: page,
      timeOnSite: now - arrivedAt,
      previousChatSummary: isReturning ? pick(SUMMARIES) : undefined,
    },
    status,
    seenAt: now,
    arrivedAt,
    lock: null,
    unread: 0,
  };
}

export function seedFixtures(): void {
  // Idempotent — clear visitors so a re-seed is a true reset.
  // NOTE: we no longer pre-populate 8 fixture visitors — the dashboard now
  // only fills from real `visitor:presence` events arriving over the bus.
  // Phase 5+ will swap this for a database-backed visitor list.
  useVisitorsStore.getState().clear();

  // Other-agent presence is now LIVE (server-broadcast `agent:presence`), so we
  // no longer seed mock peers here — the roster reflects who's actually online.
}
